import type { VehicleAction } from './actions';
import { VehicleActionTypes } from './vehicleActionTypes';

import { DisablerStatus, type Vehicle, type VehiclesByDisablerStatus } from '@cobliteam/common-types';

import { type WebData, webDataError, webDataFetching, webDataNotAsked, webDataSuccess } from '@cobliteam/api-lib';
import { DeviceGeneralStatus } from '@cobliteam/api-lib/src/__generated__/graphql';

export const initialVehiclesByDisablerStatus = {
  [DisablerStatus.DisablePending]: false,
  [DisablerStatus.EnablePending]: false,
  [DisablerStatus.Disabled]: false,
  [DisablerStatus.Enabled]: false,
  [DisablerStatus.Disabling]: false,
};

export const initialVehiclesByStatus = {
  [DeviceGeneralStatus.Normal]: { count: 0, isSelected: false },
  [DeviceGeneralStatus.Syncing]: { count: 0, isSelected: false },
  [DeviceGeneralStatus.NoSignal]: { count: 0, isSelected: false },
  [DeviceGeneralStatus.Disconnected]: { count: 0, isSelected: false },
  [DeviceGeneralStatus.Unavailable]: { count: 0, isSelected: false },
  [DeviceGeneralStatus.NoSufficientData]: { count: 0, isSelected: false },
  [DeviceGeneralStatus.LosingData]: { count: 0, isSelected: false },
  [DeviceGeneralStatus.Damaged]: { count: 0, isSelected: false },
};

export interface VehicleState {
  vehiclesWebData: WebData<Vehicle[]>;
  createEtaPageWebData: WebData<void>;
  updateVehicleTrackingWebData: WebData<void>;

  vehiclesByDisablerStatus: VehiclesByDisablerStatus;
}

export const vehiclesInitialState: VehicleState = {
  vehiclesWebData: webDataNotAsked(),
  createEtaPageWebData: webDataNotAsked(),
  updateVehicleTrackingWebData: webDataNotAsked(),

  vehiclesByDisablerStatus: initialVehiclesByDisablerStatus,
};

export const vehiclesReducer = (state: VehicleState = vehiclesInitialState, action: VehicleAction): VehicleState => {
  switch (action.type) {
    case VehicleActionTypes.LOAD_VEHICLES_REQUEST:
      return {
        ...state,
        vehiclesWebData: webDataFetching(),
      };

    case VehicleActionTypes.LOAD_VEHICLES_SUCCEEDED:
      return {
        ...state,
        vehiclesWebData: webDataSuccess(action.payload),
      };

    case VehicleActionTypes.LOAD_VEHICLES_FAILED:
      return {
        ...state,
        vehiclesWebData: webDataError(),
      };

    case VehicleActionTypes.LOAD_VEHICLES_WITH_ODOMETER_REQUEST:
      return {
        ...state,
        vehiclesWebData: webDataFetching(),
      };

    case VehicleActionTypes.LOAD_VEHICLES_WITH_ODOMETER_SUCCEEDED:
      return {
        ...state,
        vehiclesWebData: webDataSuccess(action.payload),
      };

    case VehicleActionTypes.LOAD_VEHICLES_WITH_ODOMETER_FAILED:
      return {
        ...state,
        vehiclesWebData: webDataError(),
      };

    case VehicleActionTypes.OPEN_ETA_PAGE_REQUEST:
      return {
        ...state,
        createEtaPageWebData: webDataFetching(),
      };

    case VehicleActionTypes.OPEN_ETA_PAGE_SUCCEEDED:
      return {
        ...state,
        createEtaPageWebData: webDataSuccess(undefined),
      };

    case VehicleActionTypes.OPEN_ETA_PAGE_FAILED:
      return {
        ...state,
        createEtaPageWebData: webDataError(),
      };

    case VehicleActionTypes.UPDATE_VEHICLE_TRACKING_REQUEST:
      return {
        ...state,
        updateVehicleTrackingWebData: webDataFetching(),
      };

    case VehicleActionTypes.UPDATE_VEHICLE_TRACKING_SUCCEEDED:
      return {
        ...state,
        updateVehicleTrackingWebData: webDataSuccess(undefined),
      };

    case VehicleActionTypes.UPDATE_VEHICLE_TRACKING_FAILED:
      return {
        ...state,
        updateVehicleTrackingWebData: webDataError(),
      };

    case VehicleActionTypes.FILTER_VEHICLES_BY_DISABLER_STATUS:
      return {
        ...state,
        vehiclesByDisablerStatus: action.payload,
      };

    default:
      return state;
  }
};
